import { useState } from 'react';

// Frontend validation utilities
export class ValidationUtils {
  // Email validation
  static validateEmail(email: string): { isValid: boolean; error?: string } {
    if (!email || !email.trim()) {
      return { isValid: false, error: 'Email is required' };
    }

    const trimmedEmail = email.trim().toLowerCase();
    
    // Basic format validation
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(trimmedEmail)) {
      return { isValid: false, error: 'Invalid email format' };
    }

    // Length validation
    if (trimmedEmail.length > 254) {
      return { isValid: false, error: 'Email is too long' };
    }

    // Additional checks
    const localPart = trimmedEmail.split('@')[0];
    if (localPart.length > 64) {
      return { isValid: false, error: 'Email local part is too long' };
    }

    if (trimmedEmail.startsWith('.') || trimmedEmail.endsWith('.')) {
      return { isValid: false, error: 'Email cannot start or end with a dot' };
    }

    if (trimmedEmail.includes('..')) {
      return { isValid: false, error: 'Email cannot contain consecutive dots' };
    }

    return { isValid: true };
  }

  // Password validation
  static validatePassword(password: string): { isValid: boolean; error?: string } {
    if (!password) {
      return { isValid: false, error: 'Password is required' };
    }

    // Length validation
    if (password.length < 8) {
      return { isValid: false, error: 'Password must be at least 8 characters' };
    }

    if (password.length > 128) {
      return { isValid: false, error: 'Password must be less than 128 characters' };
    }

    // Complexity validation
    if (!/[a-z]/.test(password)) {
      return { isValid: false, error: 'Password must contain at least one lowercase letter' };
    }

    if (!/[A-Z]/.test(password)) {
      return { isValid: false, error: 'Password must contain at least one uppercase letter' };
    }

    if (!/\d/.test(password)) {
      return { isValid: false, error: 'Password must contain at least one number' };
    }

    if (!/[@$!%*?&]/.test(password)) {
      return { isValid: false, error: 'Password must contain at least one special character (@$!%*?&)' };
    }

    // Check for common weak passwords
    const weakPasswords = ['password', '123456', 'qwerty', 'abc123', 'password123'];
    if (weakPasswords.includes(password.toLowerCase())) {
      return { isValid: false, error: 'Password is too common, please choose a stronger password' };
    }

    return { isValid: true };
  }

  // Name validation
  static validateName(name: string, fieldName: string): { isValid: boolean; error?: string } {
    if (!name || !name.trim()) {
      return { isValid: false, error: `${fieldName} is required` };
    }

    const trimmedName = name.trim();

    // Length validation
    if (trimmedName.length < 2) {
      return { isValid: false, error: `${fieldName} must be at least 2 characters` };
    }

    if (trimmedName.length > 50) {
      return { isValid: false, error: `${fieldName} must be less than 50 characters` };
    }

    // Character validation (only letters, spaces, hyphens, apostrophes)
    if (!/^[a-zA-Z\s\-']+$/.test(trimmedName)) {
      return { isValid: false, error: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes` };
    }

    // Check for excessive spaces or special characters
    if (trimmedName.startsWith(' ') || trimmedName.endsWith(' ')) {
      return { isValid: false, error: `${fieldName} cannot start or end with spaces` };
    }

    if (trimmedName.includes('  ')) { // Double spaces
      return { isValid: false, error: `${fieldName} cannot contain consecutive spaces` };
    }

    return { isValid: true };
  }

  // Comprehensive validation for registration
  static validateRegistrationData(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): { isValid: boolean; errors: Record<string, string> } {
    const errors: Record<string, string> = {};

    // Validate email
    const emailValidation = this.validateEmail(data.email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.error!;
    }

    // Validate password
    const passwordValidation = this.validatePassword(data.password);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.error!;
    }

    // Validate first name
    const firstNameValidation = this.validateName(data.firstName, 'First name');
    if (!firstNameValidation.isValid) {
      errors.firstName = firstNameValidation.error!;
    }

    // Validate last name
    const lastNameValidation = this.validateName(data.lastName, 'Last name');
    if (!lastNameValidation.isValid) {
      errors.lastName = lastNameValidation.error!;
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Validation for login
  static validateLoginData(data: {
    email: string;
    password: string;
  }): { isValid: boolean; errors: Record<string, string> } {
    const errors: Record<string, string> = {};

    // Validate email
    const emailValidation = this.validateEmail(data.email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.error!;
    }

    // Validate password (basic check for login)
    if (!data.password) {
      errors.password = 'Authentication credential is required';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

// React hook for form validation
export const useFormValidation = () => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateField = (field: string, value: string, type: 'email' | 'password' | 'name') => {
    let validation: { isValid: boolean; error?: string };

    switch (type) {
      case 'email':
        validation = ValidationUtils.validateEmail(value);
        break;
      case 'password':
        validation = ValidationUtils.validatePassword(value);
        break;
      case 'name':
        validation = ValidationUtils.validateName(value, field);
        break;
      default:
        validation = { isValid: true };
    }

    setErrors((prev: Record<string, string>) => ({
      ...prev,
      [field]: validation.isValid ? '' : validation.error || ''
    }));

    return validation.isValid;
  };

  const validateForm = (data: any, type: 'login' | 'register') => {
    let validation: { isValid: boolean; errors: Record<string, string> };

    if (type === 'login') {
      validation = ValidationUtils.validateLoginData(data);
    } else {
      validation = ValidationUtils.validateRegistrationData(data);
    }

    setErrors(validation.errors);
    return validation.isValid;
  };

  const clearErrors = () => {
    setErrors({});
  };

  return {
    errors,
    validateField,
    validateForm,
    clearErrors
  };
};
